import React from "react";
import { Link } from "react-router-dom";

const Research = () => {
  const researchPrinciples = [
    {
      id: 1,
      title: "Commitment to Careful Research",
      icon: "🔍",
      description:
        "The Citizen Reporters and Professional Journalist of the NAI are committed to doing careful research.",
      details: [
        "Thorough investigation and fact-checking",
        "Verification of information from multiple sources",
        "Diligent pursuit of accuracy and truth",
        "Methodical approach to information gathering",
      ],
    },
    {
      id: 2,
      title: "Transparency with PR Material",
      icon: "📢",
      description:
        "If Public Relation (PR) material is used in reporting, it must be clearly recognizable as such.",
      details: [
        "Clear identification of PR content in reports",
        "Applies to photographs and radio material",
        "Disclosure of PR center sources",
        "Maintaining editorial independence",
      ],
    },
    {
      id: 3,
      title: "Source Attribution & Protection",
      icon: "🛡️",
      description:
        "Research sources must be named in the report, providing this does not endanger the anonymity of the informant.",
      details: [
        "Proper attribution of information sources",
        "Protection of confidential informants",
        "Balance between transparency and safety",
        "Ethical handling of sensitive sources",
      ],
    },
  ];

  const researchGuidelines = [
    {
      category: "Source Verification",
      items: [
        "Cross-verify information from multiple independent sources",
        "Document all research processes and sources",
        "Verify credentials and reliability of sources",
        "Maintain records of research methodology",
      ],
    },
    {
      category: "PR Material Handling",
      items: [
        "Clearly label all PR-provided content",
        "Maintain separation between editorial and promotional content",
        "Verify facts in PR materials independently",
        "Disclose any relationships with PR entities",
      ],
    },
    {
      category: "Source Protection",
      items: [
        "Protect identity of vulnerable sources",
        "Use secure communication channels",
        "Follow legal guidelines for source protection",
        "Train in digital security for source protection",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Research Standards
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              NAI Guidelines for Ethical and Thorough Journalistic Research
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ethical Research Practices
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 text-center">
            <p className="text-lg leading-relaxed mb-6">
              NAI establishes comprehensive research standards to ensure the
              highest level of journalistic integrity, accuracy, and ethical
              conduct in news gathering and reporting.
            </p>
          </div>
        </div>
      </section>

      {/* Research Principles */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Core Research Principles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fundamental guidelines that govern research practices for NAI
              members
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {researchPrinciples.map((principle) => (
              <div
                key={principle.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 group"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-4xl">{principle.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">
                    {principle.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {principle.description}
                  </p>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                      Key Requirements:
                    </h4>
                    <ul className="space-y-2">
                      {principle.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <svg
                            className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-gray-600 text-sm">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Guidelines */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Detailed Research Guidelines
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive protocols for ethical research and information
              gathering
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {researchGuidelines.map((guideline, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  {guideline.category}
                </h3>

                <ul className="space-y-4">
                  {guideline.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-blue-600 text-sm font-bold">
                          {itemIndex + 1}
                        </span>
                      </div>
                      <span className="text-gray-700 leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PR Material Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Public Relation Material Guidelines
            </h2>
            <div className="w-24 h-1 bg-white mx-auto rounded-full"></div>
          </div>

          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-3">📸</span>
                  Visual & Audio Content
                </h3>
                <ul className="space-y-3 text-purple-100">
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Photographs from PR centers must be clearly labeled
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Radio material provided by PR must be identified
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Maintain transparency in all multimedia content
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-3">📝</span>
                  Content Disclosure
                </h3>
                <ul className="space-y-3 text-purple-100">
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Clear identification of PR-sourced information
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Separation from independent journalistic work
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-300 mr-2">•</span>
                    Avoid misleading audience about content origin
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Source Protection Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Source Protection Protocol
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Balancing transparency with the safety and confidentiality of
              informants
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  When to Name Sources
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    When source consent is obtained
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    When no safety risks are present
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    When required for credibility
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  When to Protect Anonymity
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    When informant safety is at risk
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    For vulnerable or at-risk individuals
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    When legal protection is required
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Research Compliance & Training
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            NAI provides ongoing training and resources to ensure all members
            adhere to the highest research standards
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                title: "Training Programs",
                description:
                  "Regular workshops on research methodology and ethics",
                icon: "🎓",
              },
              {
                title: "Compliance Monitoring",
                description: "Ongoing assessment of research practices",
                icon: "📊",
              },
              {
                title: "Resource Library",
                description: "Access to research tools and guidelines",
                icon: "📚",
              },
            ].map((item, index) => (
              <div key={index} className="bg-blue-50 rounded-xl p-6">
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Commit to Ethical Research
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
            Join NAI in upholding the highest standards of journalistic research
            and information integrity
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            
            <Link to ="/apply/citizen-reporter" className="border-2 border-white hover:bg-white hover:text-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
             Become a Citizen Reporter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Research;
